import { useTranslation } from "react-i18next";
import { EnvelopeIcon, MapPinIcon, PhoneIcon } from "@heroicons/react/24/outline";

import RegPlate from "../../../../shared/RegPlate";
import { ICallSheet, CallLeadType } from "../../../../../models/CallQueue";
import { TelephonyCapabilities, telHref } from "../../../../../telephony/TelephonyAdapter";
import { ageLabel, LEAD_TYPE_LABELS, vehicleTitle, websiteLabel } from "../../../../../utils/callQueue";
import { formatCallTime, LiveCallPhase } from "../../../../../utils/liveCall";
import Pill from "../Pill";

const buttonClass = "min-h-[44px] rounded-md border px-3.5 py-2 text-[13px] font-medium";

export default function CallHeader({
  sheet,
  atfName,
  phase,
  seconds,
  capabilities,
  onDial,
  onAnswered,
  onEnd,
  onHold,
  onTransfer,
}: {
  sheet: ICallSheet;
  atfName?: string | null;
  phase: LiveCallPhase;
  seconds: number;
  capabilities: TelephonyCapabilities;
  onDial: () => void;
  onAnswered: () => void;
  onEnd: () => void;
  onHold: () => void;
  onTransfer: () => void;
}) {
  const { t } = useTranslation("leads");
  const now = new Date();
  const typeTags = sheet.leadTypes.filter((type) => type !== CallLeadType.New);
  const attempt = Math.min(sheet.attempts + 1, sheet.maxAttempts);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm sm:px-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <RegPlate vrm={sheet.vrm} size="lg" />
            {typeTags.length === 0 && <Pill tone="gray">{t("Standard")}</Pill>}
            {typeTags.map((type) => <Pill key={type} tone="slate">{t(LEAD_TYPE_LABELS[type])}</Pill>)}
            {atfName && <Pill tone="slate">{atfName}</Pill>}
            <Pill tone="gray">{websiteLabel(sheet.website, sheet.source)} · {ageLabel(sheet.created, now)} {t("ago")}</Pill>
            <Pill tone="gray">{t("Attempt {{n}} of {{max}}", { n: attempt, max: sheet.maxAttempts })}</Pill>
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
            {vehicleTitle(sheet.year, sheet.make, sheet.model) || t("Unknown vehicle")}
          </h2>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-gray-700">
            {sheet.telephone && (
              <a href={telHref(sheet.telephone)} className="flex items-center gap-1.5 hover:text-primary-700">
                <PhoneIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                {sheet.telephone}
              </a>
            )}
            {sheet.email && (
              <a href={`mailto:${sheet.email}`} className="flex items-center gap-1.5 text-primary-700 underline">
                <EnvelopeIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                {sheet.email}
              </a>
            )}
            {sheet.postcode && (
              <span className="flex items-center gap-1.5">
                <MapPinIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                {sheet.postcode}
              </span>
            )}
          </div>
          {sheet.contactName && <span className="text-xs text-gray-600">{sheet.contactName}</span>}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {phase === "onCall" && (
            <span className="pr-1 text-xl font-semibold tabular-nums text-green-800">{formatCallTime(seconds)}</span>
          )}
          {phase === "available" && (
            <button type="button" onClick={onDial} disabled={!sheet.telephone} className={`${buttonClass} border-primary-600 bg-primary-600 font-semibold text-white hover:bg-primary-700 disabled:opacity-50`}>
              {t("Call")}
            </button>
          )}
          {phase === "ringing" && (
            <button type="button" onClick={onAnswered} className={`${buttonClass} border-green-600 bg-green-600 font-semibold text-white hover:bg-green-700`}>
              {t("Answered")}
            </button>
          )}
          {phase === "onCall" && capabilities.hold && (
            <button type="button" onClick={onHold} className={`${buttonClass} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>{t("Hold")}</button>
          )}
          {phase === "onCall" && capabilities.transfer && (
            <button type="button" onClick={onTransfer} className={`${buttonClass} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>{t("Transfer")}</button>
          )}
          {(phase === "ringing" || phase === "onCall") && (
            <button type="button" onClick={onEnd} className={`${buttonClass} border-red-600 bg-red-600 font-semibold text-white hover:bg-red-700`}>
              {t("End call")}
            </button>
          )}
          {phase === "wrapUp" && <span className="text-[13px] font-medium text-amber-800">{t("Save an outcome to take the next call")}</span>}
        </div>
      </div>
    </div>
  );
}
